import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { SelectValue } from 'antd/lib/select';
import { useInterval } from 'usehooks-ts';

import './index.less';

import IbButton from '../../components/common/IbButton';
import IbModal, { IbModalPosition } from '../../components/common/IbModal';
import IbTypography from '../../components/common/IbTypography';
import IbInput, { IbInputType } from '../../components/common/IbInput';
import { CheckFieldValueFnMap, useValidationForm } from '../../utils/validationUtil';
import { ValidationErrors } from '../../types';
import {
  ConnectorApiKeyAuth,
  ConnectorApiKeyAuthTypeEnum,
  ConnectorModel,
  ConnectorOAuth2Auth,
  ConnectorOAuth2AuthTypeEnum,
  ConnectorOAuthCheckStatus,
  ConnectorProblem,
  ConnectorStatus,
} from '../../../gamma-api';
import { getTranslateFunc } from '../../utils/stringUtil';
import IbFormFieldLabel from '../common/IbFormFieldLabel';
import IbSelect from '../common/IbSelect';
import { defaultApi } from '../../apis';
import IbIcon from '../common/IbIcon';
import { alertsSelectorAdd } from '../../recoil';

import {
  MAIN_CLASS_NAME,
  EMPTY_CONNECTOR,
  CONTENT_CLASS_NAME,
  EMPTY_API_KEY_SETTINGS,
  EMPTY_OAUTH_2_SETTINGS,
  OAUTH2_CALLBACK_URI,
  CALLBACK_COPY_BUTTON_CLASS_NAME,
  SHOW_CALLBACK_COPIED_TIMEOUT_MS,
  CALLBACK_URI_CLASS_NAME,
  CHECK_BUTTONS_CLASS_NAME,
  CANCEL_CHECK_BUTTON_CLASS_NAME,
  OAUTH_CHECK_INTERVAL,
} from './const';

type ConnectorForm = {
  name: string;
  url: string;
  authType: string;
  token: string;
  clientId: string;
  clientSecret: string;
};

const checkFieldValueFnMap: CheckFieldValueFnMap<ConnectorForm> = {
  name: (value: string) => !!value.length,
  url: (value: string) => !!value.length,
  authType: (value: string) =>
    !!value.length && (value === ConnectorApiKeyAuthTypeEnum.ApiKey || value === ConnectorOAuth2AuthTypeEnum.OAuth2),
  token: (value: string, form: ConnectorForm) => !!value.length || form.authType !== ConnectorApiKeyAuthTypeEnum.ApiKey,
  clientId: (value: string, form: ConnectorForm) =>
    !!value.length || form.authType !== ConnectorOAuth2AuthTypeEnum.OAuth2,
  clientSecret: (value: string, form: ConnectorForm) =>
    !!value.length || form.authType !== ConnectorOAuth2AuthTypeEnum.OAuth2,
};

interface IConnectorModalProps {
  visible: boolean;
  formConnector?: ConnectorModel;
  onCreate: (connector: ConnectorModel, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onUpdate: (connector: ConnectorModel, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onCancel: () => void;
}

const ConnectorModal: React.FC<IConnectorModalProps> = ({ visible, formConnector, onCreate, onUpdate, onCancel }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  const [checkId, setCheckId] = useState<string>();

  const [showCallbackCopied, setShowCallbackCopied] = useState(false);
  const [showCallbackCopiedTimer, setShowCallbackCopiedTimer] = useState<NodeJS.Timeout>();

  const [connector, setConnector] = useState(EMPTY_CONNECTOR);

  const initialFormValues: ConnectorForm = {
    name: formConnector?.name ?? EMPTY_CONNECTOR.name,
    url: formConnector?.settings.apis.web.url ?? EMPTY_CONNECTOR.settings.apis.web.url,
    authType: formConnector?.settings.apis.web.auth.type ?? EMPTY_CONNECTOR.settings.apis.web.auth.type,
    token: (formConnector?.settings.apis.web.auth as ConnectorApiKeyAuth)?.token ?? EMPTY_API_KEY_SETTINGS.token,
    clientId:
      (formConnector?.settings.apis.web.auth as ConnectorOAuth2Auth)?.client_id ?? EMPTY_OAUTH_2_SETTINGS.client_id,
    clientSecret:
      (formConnector?.settings.apis.web.auth as ConnectorOAuth2Auth)?.client_secret ??
      EMPTY_OAUTH_2_SETTINGS.client_secret,
  };

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    onSubmitErrors,
    validate,
    reset,
    getShowError,
    getErrorMessage,
  } = useValidationForm<ConnectorForm>(initialFormValues, checkFieldValueFnMap);

  const translate = getTranslateFunc('Connector: Modal:');

  const onVisiblePropChange = () => {
    if (!visible) return;

    setSaving(false);
    setConnector(formConnector ?? EMPTY_CONNECTOR);
    reset();
  };
  useEffect(onVisiblePropChange, [visible]);

  const onCopyCallbackClick = () => {
    void navigator.clipboard.writeText(OAUTH2_CALLBACK_URI);
    setShowCallbackCopied(true);
  };

  const onShowCodeCopiedChange = () => {
    if (showCallbackCopied) {
      !!showCallbackCopiedTimer && clearTimeout(showCallbackCopiedTimer);
      setShowCallbackCopiedTimer(setTimeout(() => setShowCallbackCopied(false), SHOW_CALLBACK_COPIED_TIMEOUT_MS));
    }
  };
  useEffect(onShowCodeCopiedChange, [showCallbackCopied]);

  const onStatusChange = (status: ConnectorStatus, problem?: ConnectorProblem | null) => {
    setConnector({ ...connector, status, problem: problem || null });
  };

  const onNameChange = (value: string) => {
    setConnector({ ...connector, name: value });
    onFieldChange('name', value);
  };

  const onNameBlur = () => onFieldBlur('name');

  const onUrlChange = (value: string) => {
    setConnector({
      ...connector,
      status: ConnectorStatus.Unknown,
      settings: {
        ...connector.settings,
        apis: { ...connector.settings.apis, web: { ...connector.settings.apis.web, url: value } },
      },
    });
    onFieldChange('url', value);
  };

  const onUrlBlur = () => onFieldBlur('url');

  const onTokenChange = (value: string) => {
    setConnector({
      ...connector,
      status: ConnectorStatus.Unknown,
      settings: {
        ...connector.settings,
        apis: {
          ...connector.settings.apis,
          web: { ...connector.settings.apis.web, auth: { type: ConnectorApiKeyAuthTypeEnum.ApiKey, token: value } },
        },
      },
    });
    onFieldChange('token', value);
  };

  const onTokenBlur = () => onFieldBlur('token');

  const onClientIdChange = (value: string) => {
    setConnector({
      ...connector,
      status: ConnectorStatus.Unknown,
      settings: {
        ...connector.settings,
        apis: {
          ...connector.settings.apis,
          web: {
            ...connector.settings.apis.web,
            auth: {
              ...(connector.settings.apis.web.auth as ConnectorOAuth2Auth),
              type: ConnectorOAuth2AuthTypeEnum.OAuth2,
              client_id: value,
            },
          },
        },
      },
    });
    onFieldChange('clientId', value);
  };

  const onClientIdBlur = () => onFieldBlur('clientId');

  const onClientSecretChange = (value: string) => {
    setConnector({
      ...connector,
      status: ConnectorStatus.Unknown,
      settings: {
        ...connector.settings,
        apis: {
          ...connector.settings.apis,
          web: {
            ...connector.settings.apis.web,
            auth: {
              ...(connector.settings.apis.web.auth as ConnectorOAuth2Auth),
              type: ConnectorOAuth2AuthTypeEnum.OAuth2,
              client_secret: value,
            },
          },
        },
      },
    });
    onFieldChange('clientSecret', value);
  };

  const onClientSecretBlur = () => onFieldBlur('clientSecret');

  const onAuthTypeChange = (value: SelectValue) => {
    if (value === ConnectorOAuth2AuthTypeEnum.OAuth2) {
      setConnector((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          apis: {
            ...prev.settings.apis,
            web: {
              ...prev.settings.apis.web,
              auth: EMPTY_OAUTH_2_SETTINGS,
            },
          },
        },
      }));
    } else {
      setConnector((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          apis: {
            ...prev.settings.apis,
            web: {
              ...prev.settings.apis.web,
              auth: EMPTY_API_KEY_SETTINGS,
            },
          },
        },
      }));
    }
    onFieldChange('authType', value as string);
  };

  const onAuthTypeBlur = () => onFieldBlur('authType');

  const onCheckButtonClick = async () => {
    if (!validate()) return;
    if (checking) return;

    setChecking(true);

    try {
      const request = { settings: connector.settings };
      const response = await defaultApi.checkConnector(request);

      if (response.data.status === ConnectorStatus.OAuthRequired) {
        if (response.data.check_id) setCheckId(response.data.check_id);
        // eslint-disable-next-line security/detect-non-literal-fs-filename
        if (response.data.auth_url) window.open(response.data.auth_url, '_blank');
      } else {
        onStatusChange(response.data.status, response.data.problem);
        setChecking(false);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Connector check failed', e);
      setChecking(false);
      addAlert({
        content: translate('Checking error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
    }
  };

  const cancelConnectorCheck = () => {
    setCheckId(undefined);
    setChecking(false);
  };

  const checkOAuthAsync = async () => {
    if (!checkId) return;
    if (document.hidden) return;

    try {
      const checkResponse = await defaultApi.pollConnectorOAuthCheck(checkId);
      const status = checkResponse.data.status;

      if (status === ConnectorOAuthCheckStatus.Pending) return;

      if (status === ConnectorOAuthCheckStatus.Complete) {
        onStatusChange(ConnectorStatus.Ready, checkResponse.data.problem);
      } else if (status === ConnectorOAuthCheckStatus.Failed) {
        onStatusChange(ConnectorStatus.Problem, checkResponse.data.problem);
      }
      cancelConnectorCheck();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('OAuth check polling failed', e);
      cancelConnectorCheck();
      addAlert({
        content: translate('Checking error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
    }
  };

  useInterval(checkOAuthAsync, OAUTH_CHECK_INTERVAL);

  const onCheckCancelButtonClick = () => cancelConnectorCheck();

  const onCreateButtonClick = async () => {
    if (!validate()) return;

    setSaving(true);
    await onCreate(connector, onSubmitErrors);
    setSaving(false);
  };

  const onUpdateButtonClick = async () => {
    if (!formConnector || !validate()) return;

    setSaving(true);
    await onUpdate(connector, onSubmitErrors);
    setSaving(false);
  };

  const renderTitle = () => (formConnector ? translate('Title: Update') : translate('Title: Create'));

  const renderCheck = () => {
    let status: 'default' | 'success' | 'error';
    let content: string;

    if (checking) {
      status = 'default';
      content = 'Check: Checking';
    } else if (connector.status === ConnectorStatus.Problem) {
      status = 'error';
      content = 'Check: Problem';
    } else if (connector.status === ConnectorStatus.Ready) {
      status = 'success';
      content = 'Check: Ready';
    } else {
      status = 'default';
      content = 'Check: Unknown';
    }

    const button = (
      <IbButton status={status} type="link" onClick={onCheckButtonClick}>
        {translate(content)}
      </IbButton>
    );

    const cancelButton = (
      <IbButton className={CANCEL_CHECK_BUTTON_CLASS_NAME} type="link" onClick={onCheckCancelButtonClick}>
        {translate('Check: Cancel')}
      </IbButton>
    );

    if (checking) {
      return (
        <>
          {button}
          {!!checkId && cancelButton}
        </>
      );
    }

    const problem = connector.problem?.details.detail;
    if (connector.status === ConnectorStatus.Problem && problem) {
      return (
        <>
          {button}
          <IbTypography.Paragraph error type="secondary">
            {problem}
          </IbTypography.Paragraph>
        </>
      );
    }

    return button;
  };

  const renderContent = () => {
    return (
      <div className={CONTENT_CLASS_NAME}>
        <IbTypography>
          <IbTypography.Paragraph type="secondary">{translate('Name: Title')}</IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.name) ? 'error' : 'default'}
            value={connector.name}
            onBlur={onNameBlur}
            onChange={onNameChange}
          />
          {getShowError(validationState.name) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.name, translate('Name: Error'))}
            </IbTypography.Paragraph>
          )}
        </IbTypography>
        <IbTypography>
          <IbFormFieldLabel hint={translate('URL: Tooltip')} label={translate('URL: Title')} />
          <IbInput
            status={getShowError(validationState.url) ? 'error' : 'default'}
            value={connector.settings.apis.web.url}
            onBlur={onUrlBlur}
            onChange={onUrlChange}
          />
          {getShowError(validationState.url) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.url, translate('URL: Error'))}
            </IbTypography.Paragraph>
          )}
        </IbTypography>
        <IbTypography>
          <IbFormFieldLabel label={translate('Auth type: Title')} />
          <IbSelect
            options={[
              { value: ConnectorApiKeyAuthTypeEnum.ApiKey, label: translate('Auth type: API Key') },
              { value: ConnectorOAuth2AuthTypeEnum.OAuth2, label: translate('Auth type: OAuth') },
            ]}
            status={getShowError(validationState.authType) ? 'error' : 'default'}
            value={connector.settings.apis.web.auth.type}
            onBlur={onAuthTypeBlur}
            onChange={onAuthTypeChange}
          />
          {getShowError(validationState.authType) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.authType, translate('Auth type: Error'))}
            </IbTypography.Paragraph>
          )}
          {connector.settings.apis.web.auth.type === ConnectorOAuth2AuthTypeEnum.OAuth2 && (
            <IbTypography.Paragraph type="descriptor">
              {translate('Auth type: OAuth: Description')}
              <div className={CALLBACK_URI_CLASS_NAME}>{OAUTH2_CALLBACK_URI}</div>
              {showCallbackCopied ? (
                <IbButton
                  className={CALLBACK_COPY_BUTTON_CLASS_NAME}
                  icon={<IbIcon iconName="check" />}
                  status="success"
                  type="link"
                />
              ) : (
                <IbButton
                  className={CALLBACK_COPY_BUTTON_CLASS_NAME}
                  icon={<IbIcon iconName="copy-one" />}
                  type="icon"
                  onClick={onCopyCallbackClick}
                />
              )}
            </IbTypography.Paragraph>
          )}
        </IbTypography>
        {connector.settings.apis.web.auth.type === ConnectorApiKeyAuthTypeEnum.ApiKey && (
          <IbTypography>
            <IbFormFieldLabel hint={translate('Token: Tooltip')} label={translate('Token: Title')} />
            <IbInput
              status={getShowError(validationState.token) ? 'error' : 'default'}
              type={IbInputType.Password}
              value={connector.settings.apis.web.auth.token}
              onBlur={onTokenBlur}
              onChange={onTokenChange}
            />
            {getShowError(validationState.token) && (
              <IbTypography.Paragraph error type="descriptor">
                {getErrorMessage(validationState.token, translate('Token: Error'))}
              </IbTypography.Paragraph>
            )}
          </IbTypography>
        )}
        {connector.settings.apis.web.auth.type === ConnectorOAuth2AuthTypeEnum.OAuth2 && (
          <>
            <IbTypography>
              <IbFormFieldLabel label={translate('Client ID: Title')} />
              <IbInput
                status={getShowError(validationState.clientId) ? 'error' : 'default'}
                value={connector.settings.apis.web.auth.client_id}
                onBlur={onClientIdBlur}
                onChange={onClientIdChange}
              />
              {getShowError(validationState.clientId) && (
                <IbTypography.Paragraph error type="descriptor">
                  {getErrorMessage(validationState.clientId, translate('Client ID: Error'))}
                </IbTypography.Paragraph>
              )}
            </IbTypography>
            <IbTypography>
              <IbFormFieldLabel label={translate('Client Secret: Title')} />
              <IbInput
                status={getShowError(validationState.clientSecret) ? 'error' : 'default'}
                type={IbInputType.Password}
                value={connector.settings.apis.web.auth.client_secret}
                onBlur={onClientSecretBlur}
                onChange={onClientSecretChange}
              />
              {getShowError(validationState.clientSecret) && (
                <IbTypography.Paragraph error type="descriptor">
                  {getErrorMessage(validationState.clientSecret, translate('Client Secret: Error'))}
                </IbTypography.Paragraph>
              )}
            </IbTypography>
          </>
        )}
        <div className={CHECK_BUTTONS_CLASS_NAME}>{renderCheck()}</div>
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <>
        {formConnector ? (
          <IbButton type="primary" onClick={onUpdateButtonClick}>
            {translate('Footer: Update')}
          </IbButton>
        ) : (
          <IbButton type="primary" onClick={onCreateButtonClick}>
            {translate('Footer: Create')}
          </IbButton>
        )}
        <IbButton type="secondary" onClick={onCancel}>
          {translate('Footer: Cancel')}
        </IbButton>
      </>
    );
  };

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderFooter()}
      loading={saving}
      position={IbModalPosition.Centered}
      title={renderTitle()}
      visible={visible}
      onCancel={onCancel}
    >
      {renderContent()}
    </IbModal>
  );
};

export default ConnectorModal;
